import { Link } from "@tanstack/react-router";
import { getGlossaryLinks } from "@/content/glossaryLinks";
import { getService } from "@/content/services";
import { getLocation } from "@/content/locations";
import { getBlogPost } from "@/content/blog";
import { getGuide } from "@/content/guides";

function topicLabel(path: string): string {
  const parts = path.split("/").filter(Boolean);
  if (parts[0] === "guides" && parts[1]) return getGuide(parts[1])?.title ?? parts[1];
  const post = parts[0] ? getBlogPost(parts[0]) : undefined;
  return post?.title ?? (parts[0] ?? path);
}

/**
 * Consistent cross-link block shown on every glossary term page:
 * commercial (services), geo (locations) and editorial (guides/blog) destinations.
 */
export function GlossaryCrossLinks({ slug, term }: { slug: string; term: string }) {
  const links = getGlossaryLinks(slug);
  const services = links.services.map((s) => getService(s)).filter(Boolean);
  const locations = links.locations.map((l) => getLocation(l)).filter(Boolean);
  const topics = links.topics.map((p) => ({ path: p, label: topicLabel(p) }));

  return (
    <nav aria-label={`Where ${term} applies on this site`} className="mt-16 rounded-3xl border border-border p-8">
      <h2 className="font-display text-2xl md:text-3xl tracking-tight">Where {term} applies</h2>
      <p className="mt-2 text-muted-foreground leading-relaxed">
        The services, Texas markets and deep-dive reading where we put this into practice.
      </p>

      <div className="mt-8 grid gap-8 md:grid-cols-3">
        <div>
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground">Related services</h3>
          <ul className="mt-3 space-y-2">
            {services.map((s) => (
              <li key={s!.slug}>
                <Link
                  to="/services/$slug/"
                  params={{ slug: s!.slug }}
                  className="text-[0.98rem] text-foreground/85 underline-offset-4 hover:text-primary hover:underline"
                >
                  {s!.title} in Austin
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground">Related markets</h3>
          <ul className="mt-3 space-y-2">
            {locations.map((l) => (
              <li key={l!.slug}>
                <Link
                  to={"/seo-agency-{$slug}" as any}
                  params={{ slug: l!.slug } as any}
                  className="text-[0.98rem] text-foreground/85 underline-offset-4 hover:text-primary hover:underline"
                >
                  SEO in {l!.city}, {l!.region}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground">Go deeper</h3>
          <ul className="mt-3 space-y-2">
            {topics.map((t) => (
              <li key={t.path}>
                <a
                  href={t.path}
                  className="text-[0.98rem] text-foreground/85 underline-offset-4 hover:text-primary hover:underline"
                >
                  {t.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
}
